import type { ApplicationConfig } from '@angular/core';
import {
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withAutoCleanupInjectors,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { providePrimaryCountryCode } from '@arya/account/data-access/primary-country-code';
import { provideDisplayCurrency } from '@arya/account/settings/preferences/data-access';
import { ThemeStore } from '@arya/account/settings/theme/data-access';
import { provideCookieConsent } from '@arya/gdpr/cookie-consent/feature';
import { provideGeocoder } from '@arya/shared/data-access/address-autocomplete';
import { PLATFORM_BASE_PATH, provideImageOptimization, provideIntentPreloading } from '@arya/shared/feature';
import { provideTurnstile } from '@arya/shared/feature/turnstile';

import { provideNoxoNavigationHistory } from '@noxo/ui/back';
import { iconResolver, provideIcons } from '@noxo/ui/icon';

import { appRoutes } from './app.routes';
import { DEV_SURFACE_PROVIDERS } from './dev-surfaces';
import { PLATFORM_BASE } from './platform-path';

export const THEME_PREFIX = 'arya';
export const THEME_NAMES = ['light', 'dark'] as const;

export const APP_CONFIG_BASE: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withAutoCleanupInjectors(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideAppInitializer(() => void inject(ThemeStore)),
    provideNoxoNavigationHistory(),
    { provide: PLATFORM_BASE_PATH, useValue: PLATFORM_BASE },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideIcons(iconResolver),
    provideImageOptimization(),
    provideIntentPreloading(),
    providePrimaryCountryCode(),
    provideDisplayCurrency(),
    provideTurnstile('0x4AAAAAAC0GQVsjaJ-3o2S8'),
    provideGeocoder({ provider: 'photon' }),
    provideCookieConsent({}),
    ...DEV_SURFACE_PROVIDERS,
  ],
};
